import routes from 'routes';

export type DesignGalleryCategoryId =
  | 'hub'
  | 'buttons'
  | 'forms'
  | 'data-display'
  | 'feedback'
  | 'overlays'
  | 'layout'
  | 'patterns'
  | 'tables'
  | 'ai'
  | 'providers'
  | 'lists-cards'
  | 'integrations'
  | 'data-retrieval'
  | 'icons'
  | 'utilities'
  | 'feature-dots'
  | 'navigation'
  | 'workflows'
  | 'submissions'
  | 'dashboard'
  | 'vat-returns'
  | 'mapping'
  | 'permissions'
  | 'auth'
  | 'billing'
  | 'antd-theme';

export type DesignGalleryManualType =
  | 'developer'
  | 'components'
  | 'integrations'
  | 'workflows';

export interface DesignGalleryCategory {
  id: DesignGalleryCategoryId;
  label: string;
  description: string;
  path: string;
  manualType: DesignGalleryManualType;
}

export interface DesignGalleryNavGroup {
  manualType: DesignGalleryManualType;
  label: string;
  description: string;
  categories: DesignGalleryCategory[];
}

export const DESIGN_GALLERY_MANUAL_TYPE_ORDER: DesignGalleryManualType[] = [
  'developer',
  'components',
  'integrations',
  'workflows',
];

export const DESIGN_GALLERY_MANUAL_TYPE_META: Record<
  DesignGalleryManualType,
  { label: string; description: string }
> = {
  developer: {
    label: 'Developer manual',
    description: 'Preferences, flags, confirms, and app-wide patterns.',
  },
  components: {
    label: 'Component library',
    description: 'Reusable UI primitives — pick before building new markup.',
  },
  integrations: {
    label: 'Integrations & data',
    description: 'Providers, connect flows, and how data enters the app.',
  },
  workflows: {
    label: 'Product workflows',
    description: 'Screen-level guides for major product areas.',
  },
};

export const DESIGN_GALLERY_CATEGORIES: DesignGalleryCategory[] = [
  // Developer manual
  {
    id: 'utilities',
    label: 'Dev manual',
    description: 'User preferences, feature flags, confirms, Redux shells, dev tricks.',
    path: routes.dev.designSystem.utilities,
    manualType: 'developer',
  },
  {
    id: 'antd-theme',
    label: 'ANTD theme audit',
    description: 'Raw antd vs Custom wrappers — magenta stress test for ConfigProvider.',
    path: routes.dev.designSystem.antdTheme,
    manualType: 'developer',
  },
  // Component library
  {
    id: 'buttons',
    label: 'Buttons',
    description: 'ScopeButton, toolbar pills, links, and audit gaps.',
    path: routes.dev.designSystem.buttons,
    manualType: 'components',
  },
  {
    id: 'forms',
    label: 'Forms & inputs',
    description: 'FormInput, selects, date pickers, switches, labels.',
    path: routes.dev.designSystem.forms,
    manualType: 'components',
  },
  {
    id: 'data-display',
    label: 'Data display',
    description: 'Tags, typography, cards, tables, charts, status pills.',
    path: routes.dev.designSystem.dataDisplay,
    manualType: 'components',
  },
  {
    id: 'feedback',
    label: 'Feedback',
    description: 'Tooltips, popconfirm, modals, messages, spinners.',
    path: routes.dev.designSystem.feedback,
    manualType: 'components',
  },
  {
    id: 'feature-dots',
    label: 'New feature marker',
    description: 'Red-dot highlights for new UI — config, dismiss, and release checklist.',
    path: routes.dev.designSystem.featureDots,
    manualType: 'components',
  },
  {
    id: 'overlays',
    label: 'Overlays',
    description: 'Drawers, tours, dropdowns, popovers, badges.',
    path: routes.dev.designSystem.overlays,
    manualType: 'components',
  },
  {
    id: 'layout',
    label: 'Layout & wrappers',
    description: 'Page wrappers, empty states, close icons — not app SideMenu.',
    path: routes.dev.designSystem.layout,
    manualType: 'components',
  },
  {
    id: 'patterns',
    label: 'Patterns & shells',
    description: 'Redux modal/drawer shells, composites, confirmations.',
    path: routes.dev.designSystem.patterns,
    manualType: 'components',
  },
  {
    id: 'tables',
    label: 'Tables & loading',
    description: 'ScopeTable sandbox, CustomTable, charts, skeletons.',
    path: routes.dev.designSystem.tables,
    manualType: 'components',
  },
  {
    id: 'lists-cards',
    label: 'Lists & cards',
    description: 'Entity list/card toggle, vertical lists, EmptyPage, card shells.',
    path: routes.dev.designSystem.listsCards,
    manualType: 'components',
  },
  {
    id: 'icons',
    label: 'Icons',
    description: 'App SVG library, brand assets, Ant Design icons.',
    path: routes.dev.designSystem.icons,
    manualType: 'components',
  },
  {
    id: 'ai',
    label: 'AI features',
    description: 'AI mapping overlay, Map with AI buttons, scan status tags.',
    path: routes.dev.designSystem.ai,
    manualType: 'components',
  },
  // Integrations & data
  {
    id: 'providers',
    label: 'Providers & logos',
    description: 'EntityButton connect tiles, table icons, ProviderLogoNav.',
    path: routes.dev.designSystem.providers,
    manualType: 'integrations',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    description: 'OAuth connect, Cin7, capture (Drive/OneDrive/email).',
    path: routes.dev.designSystem.integrations,
    manualType: 'integrations',
  },
  {
    id: 'data-retrieval',
    label: 'Data retrieval',
    description: 'Excel/CSV upload flow and data source overview.',
    path: routes.dev.designSystem.dataRetrieval,
    manualType: 'integrations',
  },
  // Product workflows
  {
    id: 'navigation',
    label: 'App navigation',
    description: 'SidePanel, SideMenu, Header, TabRouted app shell.',
    path: routes.dev.designSystem.navigation,
    manualType: 'workflows',
  },
  {
    id: 'workflows',
    label: 'Core workflows',
    description: 'What each journey does, typical click-paths, and developer references.',
    path: routes.dev.designSystem.workflows,
    manualType: 'workflows',
  },
  {
    id: 'submissions',
    label: 'Submissions',
    description: 'MainComponent views, filter views, planner.',
    path: routes.dev.designSystem.submissions,
    manualType: 'workflows',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Home grid layout and card registry.',
    path: routes.dev.designSystem.dashboard,
    manualType: 'workflows',
  },
  {
    id: 'mapping',
    label: 'Mapping',
    description: 'Add/edit return mapping wizard.',
    path: routes.dev.designSystem.mapping,
    manualType: 'workflows',
  },
  {
    id: 'vat-returns',
    label: 'VAT returns',
    description: 'Report shell, workflow CTAs, approval forms.',
    path: routes.dev.designSystem.vatReturns,
    manualType: 'workflows',
  },
  {
    id: 'permissions',
    label: 'Permissions',
    description: 'Users rights matrix and hasAdvancedUserRight.',
    path: routes.dev.designSystem.permissions,
    manualType: 'workflows',
  },
  {
    id: 'auth',
    label: 'Auth pages',
    description: 'LoginForm and SignUpForm composite layouts.',
    path: routes.dev.designSystem.auth,
    manualType: 'workflows',
  },
  {
    id: 'billing',
    label: 'Billing',
    description: 'Plans, pricing table, checkout.',
    path: routes.dev.designSystem.billing,
    manualType: 'workflows',
  },
];

export const DESIGN_GALLERY_NAV_GROUPS: DesignGalleryNavGroup[] =
  DESIGN_GALLERY_MANUAL_TYPE_ORDER.map((manualType) => ({
    manualType,
    ...DESIGN_GALLERY_MANUAL_TYPE_META[manualType],
    categories: DESIGN_GALLERY_CATEGORIES.filter(
      (category) => category.manualType === manualType
    ),
  }));

export const getDesignGalleryCategory = (
  id: DesignGalleryCategoryId
): DesignGalleryCategory | undefined =>
  DESIGN_GALLERY_CATEGORIES.find((category) => category.id === id);

export const JUNIOR_START_HERE = [
  { task: 'Form text field', use: 'FormInput + LabelWrapper' },
  { task: 'Filter chip in table', use: 'FilterSelect' },
  { task: 'Status pill on return/entity', use: 'StatusGetter or ColoredTag' },
  { task: 'Delete row action', use: 'StyledPopconfirm' },
  { task: 'Edit table row (transactions)', use: 'ScopeDropdownButton + ScopeEditSaveButton' },
  { task: 'Provider logo in table', use: 'resolveProviderIconSrc + optional ProviderLogoNav' },
  { task: 'Entity board card grid', use: 'EntityCard + Segmented list/card toggle' },
  { task: 'File upload history list', use: 'Vertical bordered rows + UploadStatusTag' },
  { task: 'Connect Xero/QBO entity', use: 'fetchAddUrlRequest (Integrations page)' },
  { task: 'Import Excel / CSV file', use: 'upload-transactions-drawer + UploadTransactionsColumnsModal' },
  { task: 'Side panel edit flow', use: 'DrawerContainer via turnDrawerOn' },
  { task: 'Centered confirm dialog', use: 'ConfirmModal' },
  { task: 'Table with filters', use: 'ScopeTable + FilterSelect' },
  { task: 'Table/chart loading', use: 'CaptureTabSkeleton or antd Skeleton' },
  { task: 'AI tax mapping CTA', use: 'ScopeButton type="dark-orange" (MapWithAIButton)' },
  { task: 'Primary form CTA', use: 'ScopeButton type="primary"' },
  { task: 'Toolbar export/action', use: 'ExportButton or IconButton (shared-lib)' },
  { task: 'Custom nav/toolbar SVG', use: 'ReactComponent from assets/images/icons' },
  { task: 'Save UI state for user', use: 'usePreferences + PreferenceSections (Dev manual)' },
  { task: 'Check feature env toggle', use: 'feature-flag-utils helpers' },
  { task: 'Disable action by entity right', use: 'hasAdvancedUserRight' },
  { task: 'Submissions table/board/calendar', use: 'MainComponent + MAIN_VIEW_MODE pref' },
];

export interface DesignGalleryItemMeta {
  id: string;
  name: string;
  importPath: string;
  whenToUse: string;
  whenNotToUse?: string;
  exampleInApp?: string;
  relatedComponents?: string[];
}

/** Central metadata registry — one entry per gallery card across all categories. */
export const DESIGN_GALLERY_ITEMS: DesignGalleryItemMeta[] = [
  // Buttons (shared-lib + audit gaps)
  {
    id: 'scope-button',
    name: 'ScopeButton',
    importPath: "import { ScopeButton } from '@monorepo/shared-lib'",
    whenToUse: 'Primary form CTAs, save/cancel, and standard actions.',
    whenNotToUse: 'Toolbar export pills — use ExportButton or IconButton.',
    exampleInApp: '100+ files',
    relatedComponents: ['ContinueButton', 'GrayButton'],
  },
  {
    id: 'continue-button',
    name: 'ContinueButton',
    importPath: "import { ContinueButton } from '@monorepo/shared-lib'",
    whenToUse: 'Wide onboarding / wizard next step.',
    exampleInApp: 'Sign-up flow',
  },
  {
    id: 'export-button',
    name: 'ExportButton',
    importPath: "import { ExportButton } from '@monorepo/shared-lib'",
    whenToUse: 'Toolbar pill with icon + label for export/actions.',
    whenNotToUse: 'Form submit — use ScopeButton type="primary".',
    exampleInApp: 'ScopeTable toolbars',
  },
  {
    id: 'icon-button-lib',
    name: 'IconButton (shared-lib)',
    importPath: "import { IconButton } from '@monorepo/shared-lib'",
    whenToUse: 'Toolbar pill with optional label — tables, entity header.',
    whenNotToUse: 'Not the same as components/Custom/IconButton.',
    relatedComponents: ['ExportButton'],
  },
  {
    id: 'icon-button-custom',
    name: 'IconButton (Custom)',
    importPath: "import IconButton from 'components/Custom/IconButton'",
    whenToUse: 'Square native button with img icon — Drive/OneDrive pickers.',
    whenNotToUse: 'Table toolbars — use shared-lib IconButton.',
  },
  {
    id: 'scope-dropdown-button',
    name: 'ScopeDropdownButton',
    importPath: "import { ScopeDropdownButton } from 'components/Custom/ScopeDropdownButton'",
    whenToUse: 'Split Edit + menu on table rows.',
    exampleInApp: 'transactions-tab/index.tsx',
  },
  {
    id: 'styled-popconfirm',
    name: 'StyledPopconfirm',
    importPath: "import { StyledPopconfirm } from 'components/Custom/Popconfirm'",
    whenToUse: 'Inline delete/action on a button — attached to trigger.',
    whenNotToUse: 'Centered save/discard — use ConfirmModal.',
    exampleInApp: '22+ table row actions',
  },
  // Forms
  {
    id: 'form-input',
    name: 'FormInput',
    importPath: "import { FormInput } from 'components/Custom/inputs'",
    whenToUse: 'Default text field in login, profile, and settings forms.',
    whenNotToUse: 'Avoid raw antd Input — inconsistent border and height.',
    exampleInApp: 'LoginForm/index.tsx',
    relatedComponents: ['LabelWrapper', 'FormPasswordInput'],
  },
  {
    id: 'form-password-input',
    name: 'FormPasswordInput',
    importPath: "import { FormPasswordInput } from 'components/Custom/inputs'",
    whenToUse: 'Password fields without strength meter.',
    exampleInApp: 'LoginForm/index.tsx',
  },
  {
    id: 'form-password-validation',
    name: 'FormPasswordInputWithValidation',
    importPath: "import { FormPasswordInputWithValidation } from 'components/Custom/inputs'",
    whenToUse: 'Sign-up or change-password with strength indicator.',
    exampleInApp: 'SignUpForm/index.tsx',
  },
  {
    id: 'round-input',
    name: 'RoundInput',
    importPath: "import { RoundInput } from 'components/Custom/inputs'",
    whenToUse: 'Pill-shaped search or filter inputs.',
  },
  {
    id: 'round-autocomplete',
    name: 'RoundAutoComplete',
    importPath: "import { RoundAutoComplete } from 'components/Custom/inputs'",
    whenToUse: 'Pill autocomplete (header search patterns).',
    exampleInApp: 'MainComponent/Header search',
  },
  {
    id: 'form-textarea',
    name: 'FormTextArea',
    importPath: "import { FormTextArea } from 'components/Custom/inputs'",
    whenToUse: 'Multi-line notes and descriptions.',
  },
  {
    id: 'numeric-input',
    name: 'NumericInput',
    importPath: "import { NumericInput } from 'components/Custom/inputs'",
    whenToUse: 'Numeric fields with Scope error styling.',
  },
  {
    id: 'otp-input',
    name: 'OTPInput',
    importPath: "import { OTPInput } from 'components/Custom/inputs'",
    whenToUse: 'MFA / one-time code entry.',
    exampleInApp: 'AccountComponent MFA setup',
  },
  {
    id: 'form-select',
    name: 'FormSelect',
    importPath: "import { FormSelect } from 'components/Custom/selects'",
    whenToUse: 'Standard dropdown in forms and filters.',
    exampleInApp: '24+ files',
  },
  {
    id: 'filter-select',
    name: 'FilterSelect',
    importPath: "import { FilterSelect } from 'components/Custom/selects'",
    whenToUse: 'Table filter chips in ScopeTable toolbar.',
    exampleInApp: 'Custom/table.tsx',
  },
  {
    id: 'round-search-select',
    name: 'RoundSearchSelect',
    importPath: "import { RoundSearchSelect } from 'components/Custom/selects'",
    whenToUse: 'Pill select for search-style pickers.',
  },
  {
    id: 'form-date-picker',
    name: 'FormDatePicker',
    importPath: "import { FormDatePicker } from 'components/Custom/datePicker'",
    whenToUse: 'Single date in forms.',
  },
  {
    id: 'form-range-picker',
    name: 'FormRangePicker',
    importPath: "import { FormRangePicker } from 'components/Custom/datePicker'",
    whenToUse: 'Date range filters.',
  },
  {
    id: 'label-wrapper',
    name: 'LabelWrapper',
    importPath: "import { LabelWrapper } from 'components/Custom/labels'",
    whenToUse: 'Muted label row above form controls.',
    relatedComponents: ['FormInput'],
  },
  {
    id: 'switch-labeled',
    name: 'SwitchLabeled',
    importPath: "import SwitchLabeled from 'components/Custom/SwitchLabeled'",
    whenToUse: 'Setting toggle with label and help tooltip icon.',
    exampleInApp: 'Organisation settings',
  },
  {
    id: 'gt-input-legacy',
    name: 'GTInput (legacy)',
    importPath: "import { GTInput } from 'core/GTInput'",
    whenToUse: 'Legacy only — do not use for new work.',
    whenNotToUse: 'New forms — use FormInput instead.',
  },
  // Data display
  {
    id: 'colored-tag',
    name: 'ColoredTag',
    importPath: "import { ColoredTag } from 'components/Custom/spans'",
    whenToUse: 'Custom-colored status or category pill.',
    relatedComponents: ['StatusGetter'],
  },
  {
    id: 'strong-tag',
    name: 'StrongTag',
    importPath: "import { StrongTag } from 'components/Custom/spans'",
    whenToUse: 'Clickable underlined tag-style action.',
  },
  {
    id: 'upload-status-tag',
    name: 'UploadStatusTag',
    importPath: "import { UploadStatusTag } from 'components/Custom/UploadStatusTag'",
    whenToUse: 'Upload / file processing status on transactions.',
    exampleInApp: 'transactions-tab',
  },
  {
    id: 'status-getter',
    name: 'StatusGetter',
    importPath: "import { StatusGetter } from 'utils/status-getter'",
    whenToUse: 'Return/submission status from enum — prefer over manual ColoredTag.',
    exampleInApp: 'MainComponent board, VAT reports',
  },
  {
    id: 'styled-card',
    name: 'StyledCard',
    importPath: "import { StyledCard } from 'components/Custom/card'",
    whenToUse: 'Primary content card — billing, settings, panels.',
    exampleInApp: 'Billing/index.tsx',
  },
  {
    id: 'gray-card',
    name: 'GrayCard',
    importPath: "import { GrayCard } from 'components/Custom/card'",
    whenToUse: 'Speech-bubble style callout.',
  },
  {
    id: 'base-card',
    name: 'BaseCard',
    importPath: "import { BaseCard } from 'components/HomeComponent/cards/BaseCard'",
    whenToUse: 'Dashboard home card shell.',
    exampleInApp: 'HomeComponent cards',
  },
  {
    id: 'scope-table',
    name: 'ScopeTable',
    importPath: "import { ScopeTable } from 'components/Custom/table'",
    whenToUse: 'Data tables with FilterSelect chips, pagination, column customization.',
    whenNotToUse: 'Do not rebuild table filtering — extend ScopeTable.',
    exampleInApp: 'Custom/table.tsx (33 files)',
  },
  // Feedback
  {
    id: 'styled-tooltip',
    name: 'StyledTooltip',
    importPath: "import { StyledTooltip } from 'components/Custom/Tooltip'",
    whenToUse: 'White tooltip without arrow — most common.',
    exampleInApp: '32+ files',
  },
  {
    id: 'styled-static-tooltip',
    name: 'StyledStaticTooltip',
    importPath: "import { StyledStaticTooltip } from 'components/Custom/staticTooltip'",
    whenToUse: 'Tooltip with arrow when pointer context needed.',
  },
  {
    id: 'question-mark-tooltip',
    name: 'QuestionMarkTooltip',
    importPath: "import { QuestionMarkTooltip } from 'components/Custom/QuestionMarkTooltip'",
    whenToUse: 'Teal ? icon beside labels and settings.',
    exampleInApp: 'SwitchLabeled, forms',
  },
  {
    id: 'confirm-modal',
    name: 'ConfirmModal',
    importPath: "import { ConfirmModal, ConfirmModalFooter } from 'components/Custom/ConfirmModal'",
    whenToUse: 'Centered unsaved-changes / discard dialogs.',
    whenNotToUse: 'Inline row delete — use StyledPopconfirm.',
  },
  {
    id: 'message-box',
    name: 'MessageBoxContainer',
    importPath: "import { MessageBoxContainer, MessageStates } from 'containers/MessageBox'",
    whenToUse: 'Global toast notifications via turnMessageOn dispatch.',
    whenNotToUse: 'Not for inline field errors — use form validation.',
    exampleInApp: 'App.tsx (global)',
  },
  {
    id: 'active-step-spinner',
    name: 'ActiveStepSpinner',
    importPath: "import { ActiveStepSpinner } from 'components/Custom/ActiveStepSpinner'",
    whenToUse: 'Step loading indicator in multi-step flows.',
  },
  // Overlays
  {
    id: 'scope-tour',
    name: 'ScopeTour',
    importPath: "import { ScopeTour } from 'components/Custom/ScopeTour'",
    whenToUse: 'Guided product tours with gold/teal step buttons.',
    exampleInApp: 'Entity guide, mapping guide',
  },
  {
    id: 'feature-dot-badge',
    name: 'FeatureDotBadge',
    importPath: "import { FeatureDotBadge } from 'components/Custom/FeatureDotBadge'",
    whenToUse: 'Highlight new features on nav items until dismissed.',
  },
  {
    id: 'drawer-container',
    name: 'DrawerContainer',
    importPath: "import { DrawerContainer } from 'containers/DrawerContainer'",
    whenToUse: 'Side panels via turnDrawerOn — edit flows, guides, forms.',
    whenNotToUse: 'Do not use raw antd Drawer directly.',
    exampleInApp: 'App.tsx global shell',
  },
  // Layout
  {
    id: 'page-wrapper',
    name: 'PageWrapper / FormWrapper',
    importPath: "import { PageWrapper, FormWrapper } from 'components/Custom/wrappers'",
    whenToUse: 'Auth and onboarding pages — teal background with centered form column.',
    whenNotToUse: 'Not for logged-in app pages.',
    exampleInApp: 'LoginForm, SignUp pages',
  },
  {
    id: 'entity-details-wrapper',
    name: 'EntityDetailsWrapper',
    importPath: "import { EntityDetailsWrapper } from 'components/Custom/wrappers'",
    whenToUse: 'Entity detail pages with fixed tab strip and top toolbar slot.',
    exampleInApp: 'OrganisationDetails/index.tsx',
  },
  {
    id: 'profile-wrapper',
    name: 'ProfileWrapper',
    importPath: "import { ProfileWrapper } from 'components/Custom/wrappers'",
    whenToUse: 'Account, billing, and settings pages with gray surface.',
    exampleInApp: 'AccountPage, BillingPage',
  },
  {
    id: 'empty-page',
    name: 'EmptyPage',
    importPath: "import { EmptyPage } from 'containers/EmptyPage'",
    whenToUse: 'Dashed callout when a section has no data yet.',
    exampleInApp: 'Entities, returns lists',
  },
  {
    id: 'default-icon-close',
    name: 'DefaultIconClose',
    importPath: "import { DefaultIconClose } from 'shared/styles/common'",
    whenToUse: 'Standard × close icon in drawers, modals, and guides.',
    exampleInApp: 'DrawerContainer, Entity guide',
  },
  {
    id: 'provider-logo-nav',
    name: 'ProviderLogoNav',
    importPath: "import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav'",
    whenToUse: 'Wrap provider logos — external link or in-app Transactions route.',
    exampleInApp: 'Entity/index.tsx',
  },
  // Patterns
  {
    id: 'modal-window-container',
    name: 'ModalWindowContainer',
    importPath: "import { turnModalOn } from 'store/app/actions'",
    whenToUse: 'Centered dialogs — forms, warnings, upgrade upsells.',
    whenNotToUse: 'Inline row delete → StyledPopconfirm instead.',
    exampleInApp: 'Entity delete, dashboard unsaved',
  },
  {
    id: 'confirm-box-container',
    name: 'ConfirmBoxContainer',
    importPath: "import { ConfirmBoxContainer } from 'containers/ConfirmBox'",
    whenToUse: 'Confirm/discard flows inside turnModalOn shell.',
    exampleInApp: 'HomeComponent unsaved layout',
  },
  {
    id: 'upgrade-message',
    name: 'UpgradeMessage',
    importPath: "import { UpgradeMessage } from 'containers/UpgradeMessage'",
    whenToUse: 'Plan limit or feature gate — shown inside turnModalOn shell.',
    whenNotToUse: 'Simple yes/no on a row → StyledPopconfirm.',
    exampleInApp: 'Entity delete, invite user',
  },
  {
    id: 'add-return-button',
    name: 'AddReturnButton',
    importPath: "import { AdddReturnButton } from 'utils/AddReturnButton'",
    whenToUse: 'Add return CTA on entity cards — respects rights and connection state.',
    exampleInApp: 'Entity/index.tsx',
  },
  {
    id: 'entity-card',
    name: 'EntityCard',
    importPath: "import { EntityCard } from 'components/Entity/EntityCard'",
    whenToUse: 'Entity board card — composed with render props per section.',
    whenNotToUse: 'Not a generic card — use StyledCard for settings.',
    exampleInApp: 'Entity/index.tsx',
  },
  // Additional shared-lib buttons (button gallery tiers)
  {
    id: 'sso-login-button',
    name: 'SsoProviderButton',
    importPath: "import { SsoProviderButton, SsoProviderList } from '@monorepo/shared-lib'",
    whenToUse: 'OAuth / SSO sign-in pills on auth pages. Intuit hover stays branded.',
    exampleInApp: 'LoginForm / SignUpForm SSO providers',
  },
  {
    id: 'select-button',
    name: 'SelectButton',
    importPath: "import { SelectButton } from '@monorepo/shared-lib'",
    whenToUse: 'Selectable pill toggles in toolbars.',
  },
  {
    id: 'gray-button',
    name: 'GrayButton',
    importPath: "import { GrayButton } from '@monorepo/shared-lib'",
    whenToUse: 'Secondary muted actions on colored backgrounds.',
  },
  {
    id: 'yellow-button',
    name: 'YellowButton',
    importPath: "import { YellowButton } from '@monorepo/shared-lib'",
    whenToUse: 'Highlight / warning CTA on dashboards.',
  },
  {
    id: 'toggle-button',
    name: 'ToggleButton',
    importPath: "import { ToggleButton } from '@monorepo/shared-lib'",
    whenToUse: 'Binary toolbar toggles (e.g. view modes).',
  },
  {
    id: 'round-add-button',
    name: 'RoundAddButton',
    importPath: "import { RoundAddButton } from '@monorepo/shared-lib'",
    whenToUse: 'Circular + add control in compact toolbars.',
  },
  {
    id: 'context-button',
    name: 'ContextButton',
    importPath: "import { ContextButton } from '@monorepo/shared-lib'",
    whenToUse: 'Context menu trigger styled as Scope pill.',
  },
  {
    id: 'link-button',
    name: 'LinkButton',
    importPath: "import { LinkButton } from '@monorepo/shared-lib'",
    whenToUse: 'Text-style button that looks like a link.',
  },
  {
    id: 'entity-button',
    name: 'EntityButton',
    importPath: "import { EntityButton } from '@monorepo/shared-lib'",
    whenToUse: 'Branded entity connection buttons (Xero, QBO logos).',
  },
  {
    id: 'scope-edit-save-button',
    name: 'ScopeEditSaveButton',
    importPath: "import { ScopeEditSaveButton } from 'components/Custom/ScopeDropdownButton'",
    whenToUse: 'Inline edit mode save control beside cancel icon.',
    exampleInApp: 'transactions-tab edit drawer',
  },
  {
    id: 'entity-transactions-toolbar-icon',
    name: 'EntityTransactionsToolbarIconButton',
    importPath: "from 'components/OrganisationDetails/transactions-tab/styles'",
    whenToUse: 'Transactions tab toolbar icon pills — local styled, not shared-lib.',
    whenNotToUse: 'New toolbars — prefer shared-lib IconButton unless matching transactions UI.',
    exampleInApp: 'transactions-tab/styles.tsx',
  },
  // Tables & loading
  {
    id: 'scope-table-sandbox',
    name: 'ScopeTable',
    importPath: "import { ScopeTable } from 'components/Custom/table'",
    whenToUse: 'Primary data table with filters, pagination, column drawer, and header sorting.',
    whenNotToUse: 'Pricing comparison grids — use CustomTable.',
    exampleInApp: 'Custom/table.tsx',
    relatedComponents: ['FilterSelect', 'ColumnCustomizationDrawer'],
  },
  {
    id: 'scope-table-sort',
    name: 'ScopeTable column sorting',
    importPath: "column.sorter + defaultSort on ScopeTable columns",
    whenToUse: 'Click column header to toggle asc/desc — add sorter fn per sortable column.',
    whenNotToUse: 'Without sorter on column def, header is not sortable.',
    exampleInApp: 'Billing/index.tsx',
  },
  {
    id: 'custom-table',
    name: 'CustomTable',
    importPath: "import { CustomTable } from 'components/Custom/custom-table'",
    whenToUse: 'Styled antd Table for pricing/feature matrices.',
    exampleInApp: 'Billing/pricing.tsx',
  },
  {
    id: 'column-customization-drawer',
    name: 'ColumnCustomizationDrawer',
    importPath: "import { ColumnCustomizationDrawer } from 'components/Custom/ColumnCustomizationDrawer'",
    whenToUse: 'Column show/hide/reorder inside turnDrawerOn(CUSTOMIZE_COLUMNS).',
    exampleInApp: 'transactions-tab/settings-drawer.tsx',
  },
  {
    id: 'capture-tab-skeleton',
    name: 'CaptureTabSkeleton',
    importPath: "import { CaptureTabSkeleton } from 'components/OrganisationDetails/capture-tab-skeleton'",
    whenToUse: 'Capture Documents tab loading shell with shimmer bones.',
    exampleInApp: 'OrganisationDetails/index.tsx',
  },
  {
    id: 'line-chart',
    name: 'LineChart',
    importPath: "import { LineChart } from 'components/Custom/lineChart'",
    whenToUse: 'Dashboard SVG line trends.',
    exampleInApp: 'SubmittedReturnsChartCard',
  },
  {
    id: 'scope-pie-chart',
    name: 'ScopePieChart',
    importPath: "import { ScopePieChart } from 'components/Custom/pieChart'",
    whenToUse: 'Dashboard donut with optional ReturnsPopover slices.',
    exampleInApp: 'HomeComponent calendar cards',
  },
  {
    id: 'returns-popover',
    name: 'ReturnsPopover',
    importPath: "import { ReturnsPopover } from 'components/Custom/ReturnsPopover'",
    whenToUse: 'Popover listing returns from charts or calendar cells.',
    exampleInApp: 'CalendarCard',
  },
  // AI
  {
    id: 'map-with-ai-button',
    name: 'MapWithAIButton',
    importPath: 'MappingForm/map-tax-rates.tsx (styled ScopeButton)',
    whenToUse: 'Start AI-assisted tax rate mapping — use type="dark-orange".',
    whenNotToUse: 'Do not use type="default" — that shows teal outline, not production gold.',
    exampleInApp: 'MappingForm/map-tax-rates.tsx',
  },
  {
    id: 'fuzzy-mapping-button',
    name: 'FuzzyMappingButton',
    importPath: 'upload-transactions-columns-modal.tsx (styled ScopeButton)',
    whenToUse: 'Fuzzy-match CTA in upload/mapping flows — type="dark-orange".',
    exampleInApp: 'upload-transactions-columns-modal.tsx',
  },
  {
    id: 'ai-mapping-steps-overlay',
    name: 'AiMappingStepsOverlay',
    importPath: "import { AiMappingStepsOverlay } from 'components/MappingForm/ai-mapping-steps-overlay'",
    whenToUse: 'Multi-step overlay while AI mapping runs.',
    exampleInApp: 'returns mapWithAI flow',
  },
  {
    id: 'upload-status-ai',
    name: 'UploadStatusTag (AI pipeline)',
    importPath: "import { UploadStatusTag } from 'components/Custom/UploadStatusTag'",
    whenToUse: 'WAITING_FOR_AI / BUILDING_TRANSACTIONS on file rows.',
    exampleInApp: 'transactions-tab upload list',
  },
  // Transactions table edit
  {
    id: 'transactions-edit-dropdown',
    name: 'ScopeDropdownButton (transactions Edit)',
    importPath: "import { ScopeDropdownButton } from 'components/Custom/ScopeDropdownButton'",
    whenToUse: 'Fixed-right actions column — Edit + menu on transaction rows.',
    exampleInApp: 'transactions-tab/index.tsx',
    relatedComponents: ['ScopeEditSaveButton', 'ScopeEditCancelIcon'],
  },
  {
    id: 'transactions-inline-save',
    name: 'ScopeEditSaveButton',
    importPath: "import { ScopeEditSaveButton, ScopeEditCancelIcon } from 'components/Custom/ScopeDropdownButton'",
    whenToUse: 'Inline edit mode Save/Cancel in table actions cell.',
    exampleInApp: 'transactions-tab inline edit',
  },
  // Providers
  {
    id: 'entity-button-connect',
    name: 'EntityButton',
    importPath: "import { EntityButton } from '@monorepo/shared-lib'",
    whenToUse: 'Large connect tiles in Connect Entity drawer.',
    exampleInApp: 'ConnectEntity/index.tsx',
  },
  {
    id: 'provider-icon-src',
    name: 'resolveProviderIconSrc',
    importPath: "import { resolveProviderIconSrc, PROVIDER_ICON_SRC } from 'utils/resolve-provider-icon-src'",
    whenToUse: 'Small table/list provider logos (22px PNGs).',
    exampleInApp: 'transactions-tab, VAT reports, entity lists',
  },
  {
    id: 'provider-logo-nav',
    name: 'ProviderLogoNav',
    importPath: "import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav'",
    whenToUse: 'Clickable logo — external provider app or in-app Transactions tab.',
    exampleInApp: 'Entity/index.tsx, planner',
  },
  // Lists & cards
  {
    id: 'entity-list-card-toggle',
    name: 'List / card Segmented toggle',
    importPath: 'antd Segmented — Entity/index.tsx ENTITIES_VIEW_LAYOUT preference',
    whenToUse: 'Same dataset as table rows or card grid — user preference persisted.',
    exampleInApp: 'Entity/index.tsx toolbar',
  },
  {
    id: 'vertical-file-list',
    name: 'Capture file list rows',
    importPath: 'drive-upload-history.tsx ScrollableFileList pattern',
    whenToUse: 'Stack of bordered rows with meta + UploadStatusTag.',
    exampleInApp: 'Data Capture History drawer',
  },
  // Integrations
  {
    id: 'fetch-add-url',
    name: 'fetchAddUrlRequest',
    importPath: "import { fetchAddUrlRequest } from 'store/organisation/actions'",
    whenToUse: 'OAuth redirect to connect Xero, QuickBooks, or Zoho.',
    exampleInApp: 'ConnectEntity/index.tsx',
  },
  {
    id: 'add-organisation-manual',
    name: 'addOrganisationRequest',
    importPath: "import { addOrganisationRequest } from 'store/organisation/actions'",
    whenToUse: 'Create Excel or Receipts entity without OAuth.',
    exampleInApp: 'ConnectEntity onConnectExcel',
  },
  {
    id: 'connect-folder-capture',
    name: 'ConnectFolder capture',
    importPath: 'components/OrganisationDetails/ConnectFolder.tsx',
    whenToUse: 'Google Drive, OneDrive folder watch, or scan email for receipts.',
    exampleInApp: 'OrganisationDetails Capture tab',
  },
  // Data retrieval
  {
    id: 'upload-columns-modal',
    name: 'UploadTransactionsColumnsModal',
    importPath: 'upload-transactions-columns-modal.tsx',
    whenToUse: 'Map spreadsheet columns after file upload in the upload drawer.',
    exampleInApp: 'EXCEL_TRANSACTIONS modal window',
  },
  {
    id: 'excel-upload-pipeline',
    name: 'Excel import pipeline',
    importPath: 'upload-transactions-drawer.tsx → setUploadTransactionsTemp → uploadExcelTransactionsSaga',
    whenToUse: 'Excel entity imports transactions from spreadsheet export.',
    exampleInApp: 'OrganisationDetails transactions tab',
  },
  {
    id: 'use-preferences',
    name: 'usePreferences',
    importPath: "import { usePreferences, PreferenceActions, PreferenceSections } from 'utils/preferences'",
    whenToUse: 'Persist per-user UI state to backend — view modes, columns, dismissed guides.',
    whenNotToUse: 'Not localStorage — always use preferences API.',
    exampleInApp: 'Entity/index.tsx, MainComponent, transactions-tab',
  },
  {
    id: 'icon-context-button',
    name: 'IconContextButton',
    importPath: "import { IconContextButton } from '@monorepo/shared-lib'",
    whenToUse: 'Overflow / context menu pill in VAT and Users toolbars.',
    exampleInApp: 'Vat/Report, UsersComponent',
  },
  {
    id: 'side-menu',
    name: 'SideMenu',
    importPath: 'components/Custom/sideMenu.tsx',
    whenToUse: 'Primary app navigation with iconHome, iconReturns, etc.',
    exampleInApp: 'containers/SidePanel',
  },
  {
    id: 'main-component',
    name: 'MainComponent',
    importPath: 'components/MainComponent/index.tsx',
    whenToUse: 'Submissions workspace — table, board, calendar + filter views.',
    exampleInApp: 'pages/submissions',
  },
  {
    id: 'home-dashboard',
    name: 'HomeComponent',
    importPath: 'components/HomeComponent/index.tsx',
    whenToUse: 'Customizable dashboard grid — CARD_REGISTRY drives tiles.',
    exampleInApp: 'pages/home',
  },
  {
    id: 'vat-get-next-button',
    name: 'getNextButton',
    importPath: 'components/Vat/Report/get-next-button.tsx',
    whenToUse: 'VAT return workflow primary CTA by status and role.',
    exampleInApp: 'Vat/Report/index.tsx',
  },
  {
    id: 'users-rights',
    name: 'UsersComponent rights',
    importPath: 'components/UsersComponent/rights.tsx',
    whenToUse: 'Entity advanced-rights matrix UI.',
    exampleInApp: 'pages/users',
  },
  {
    id: 'app-icon-library',
    name: 'App icon library',
    importPath: "import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg'",
    whenToUse: 'Browse /dev/design-system/icons — click tile to copy import.',
    exampleInApp: 'assets/images/icons',
  },
  {
    id: 'open-global-confirm',
    name: 'openGlobalConfirm',
    importPath: 'utils/open-global-confirm.tsx',
    whenToUse: 'Centered confirm from saga or non-React code via turnModalOn.',
    exampleInApp: 'Organisation delete flows',
  },
];

export const DESIGN_GALLERY_ITEMS_BY_ID: Record<string, DesignGalleryItemMeta> =
  DESIGN_GALLERY_ITEMS.reduce(
    (acc, item) => {
      acc[item.id] = item;
      return acc;
    },
    {} as Record<string, DesignGalleryItemMeta>
  );

export const getGalleryItemMeta = (id: string): DesignGalleryItemMeta | undefined =>
  DESIGN_GALLERY_ITEMS_BY_ID[id];
